import { useMemo } from 'react';
import { useAppSelector } from '../../app/hooks';
import { selectAuthUser } from './authSlice';
import { CH } from '../../constants/channels';

/**
 * A client-side mirror of the server's route guards.
 *
 * The API is the authority — every write is guarded there, and this changes none of
 * that. What it does is stop the UI offering buttons the server will refuse: a Viewer
 * seeing "Add Lead" only to be handed a 403 is the UI lying about what it can do.
 *
 * Kept in step with `backend/src/routes/*` — `canEdit` is Admin|Manager on leads,
 * accounts, influencers and options; the team is Admin-only.
 */

/** Admin and Manager may change channel data; everyone else is read-only. */
const EDIT_ROLES = ['Admin', 'Manager'];

export function usePermissions() {
  const user = useAppSelector(selectAuthUser);

  return useMemo(() => {
    const role = user?.role || null;
    const isAdmin = role === 'Admin';
    const channels = user?.channels;

    // "all" is a wildcard, not a list — expand it so callers can just filter
    const allowed = channels === 'all' ? CH.map((c) => c.id) : Array.isArray(channels) ? channels : [];

    return {
      role,
      isAdmin,
      /** leads, accounts, influencer tiers, dropdown options */
      canEdit: EDIT_ROLES.includes(role),
      /** add, edit, disable or remove a teammate */
      canManageTeam: isAdmin,
      allowedChannels: allowed,
      canAccess: (id) => allowed.includes(id),
      /** the channels this user should even be shown */
      visibleChannels: CH.filter((c) => allowed.includes(c.id)),
    };
  }, [user]);
}

/** Explains why a control is disabled, for a title tooltip. */
export const readOnlyHint = (role) =>
  role ? `${role} access is read-only here` : 'Read-only';
